/** Strings of the mission screens; merged into `ca` by src/i18n/ca.ts. */
/** The ground a type needs, named as a thing: "un aeròdrom", "un pont". */
function groundName(kind: string): string {
  const names: Record<string, string> = {
    airfield: 'un aeròdrom',
    site: 'una instal·lació militar',
    bridge: 'un pont',
    road: 'carreteres',
    settlement: 'poblacions',
    high_ground: 'terreny elevat',
    water: 'costa',
    flat: 'terreny pla',
  };
  return names[kind] ?? kind;
}

/** The same ground, named as what a terrain is missing: "cap aeròdrom". */
function missingGround(kind: string): string {
  const names: Record<string, string> = {
    airfield: 'cap aeròdrom',
    site: 'cap instal·lació militar',
    bridge: 'cap pont',
    road: 'carreteres',
    settlement: 'poblacions',
    high_ground: 'terreny elevat',
    water: 'costa',
    flat: 'terreny pla',
  };
  return names[kind] ?? kind;
}

export const ca_mission = {
  missionUi: {
    rail: {
      server: 'Servidor',
      map: 'Mapa',
      enemy: 'Enemic',
      // «Dificultat» since 2026-10-12: the step asks how many enemies and how good, then what is known.
      intel: 'Dificultat',
      type: 'Tipus',
      ours: 'Nosaltres',
      start: 'Sortida',
      // «Crea» since 2026-10-12: the step ends in the mission, and a session is only one of its answers.
      decide: 'Crea',
    },
    /** Step 1: the server the night is played on. The server is the mod set. */
    server: {
      title: 'On es jugarà',
      pick: 'Servidor (conjunt de mods)',
      era: 'Època de la missió',
      eraHint:
        'Aquest servidor pot ambientar nits de més d’una època. En depenen la base, els objectes dels objectius i els mitjans i suports que tenen sentit.',
      blurb:
        'Tria el servidor de la partida. Els mods, els mapes, les faccions i tot el que ve després surten del que aquest servidor té carregat ara.',
      running: 'engegat',
      none: 'La plataforma encara no pot fer servir cap servidor.',
      missing: 'No hi ha el servidor que busques?',
      missingAsk: 'Demana a qui gestiona els servidors que l’enllaci o en creï un.',
      dlcs: 'DLC:',
      /** The one-line summary of what a server runs, in the details panel. */
      summaryMods: (n: number) => (n === 1 ? '1 mod' : `${n} mods`),
      summaryDlcs: (n: number) => (n === 1 ? '1 DLC' : `${n} DLC`),
      showMods: 'Veure’ls',
      /** The servers that cannot be chosen yet, folded under the ones that can. */
      moreToLink: (n: number) =>
        n === 1 ? '1 servidor més per enllaçar' : `${n} servidors més per enllaçar`,
      scanned: 'Contingut llegit: es pot fer una missió per a aquest servidor.',
      scanning:
        'S’està llegint el contingut d’aquests mods (uns minuts). Aquesta pàgina s’actualitza sola.',
      notScanned:
        'Els mods d’aquest servidor han canviat o no s’han llegit mai: cal llegir-ne el contingut abans de fer-hi una missió.',
      notScannedShort: 'mods sense llegir',
      scan: 'Llegeix el contingut dels mods',
      scanStarting: 'Engegant la lectura…',
      scanFailed: (why: string) => `La lectura no ha pogut començar: ${why}`,
      scanAsk: 'Demana-ho a qui gestiona els servidors.',
      scanHint:
        'Es fa en un servidor temporal de la plataforma amb els mateixos mods i DLC; el servidor triat no es toca.',
    },
    /**
     * A list a step needs that did not arrive. Never the sentence for "there is
     * none": a refusal names the permission and whom to ask, anything else says
     * it could not be read, and both offer to ask again.
     */
    load: {
      what: {
        servers: 'els servidors',
        terrains: 'els mapes',
        factions: 'les faccions',
        recipes: 'els tipus de missió',
      },
      forbidden: (what: string) =>
        `Aquest compte no pot veure ${what}. Demana a un administrador el permís de «Creador de missions»; un cop te’l doni, no cal tornar a entrar.`,
      failed: (what: string) => `No s’han pogut llegir ${what}.`,
      retry: 'Torna-ho a provar',
    },
    /**
     * Step 4: how strong the enemy is and what is known about them. The intel
     * words are the ones the group uses on a map, not the ones the code uses:
     * a "taca" is a zone, a "punt fort" is a strongpoint, and a mark that
     * points at nobody is "informació que no s'aguanta" rather than a lie.
     */
    intel: {
      title: 'Què en sabem',
      blurb:
        'El que hi hagi marcat surt d’on són els enemics de veritat, però desplaçat i eixamplat a posta. Com pitjor sigui la informació, més ampla es dibuixa.',
      area: 'Marca la zona on es preveu l’enemic',
      strongpoints: 'Marca els punts forts previstos',
      quality: 'Qualitat de la informació',
      qualityHint:
        'Una lectura bona cau a 150 m del que hi ha; una de vaga, a 600 i amb la taca molt més gran.',
      qualityNames: {
        vague: 'Vaga',
        reasonable: 'Raonable',
        good: 'Bona',
      } as Record<string, string>,
      marks: 'Quants punts forts es marquen',
      strongpointsMost: (n: number) =>
        n === 0
          ? 'En aquest tipus de missió l’enemic no defensa cap lloc fix: no hi ha punts forts per marcar.'
          : n === 1
            ? 'La missió tindrà 1 punt fort: és el màxim que es pot marcar.'
            : `La missió tindrà fins a ${n} punts forts: és el màxim que es pot marcar.`,
      wrong: 'Quantes marques no s’aguanten',
      wrongHint:
        'Assenyalen un lloc on no hi ha ningú, i es dibuixen igual que les bones. Zero vol dir que tot el que hi ha al mapa és cert.',
      /** One short sentence under each control; the long ones above sit under «Com funciona». */
      areaHelp: 'Una taca al mapa on es preveu l’enemic.',
      strongpointsHelp: 'Els llocs on s’espera resistència.',
      qualityHelp: 'Com de lluny cau cada marca del que hi ha de veritat.',
      wrongHelp: 'Marques que assenyalen un lloc buit.',
      howItWorks: 'Com funciona',
      /**
       * Card 1, «Quants enemics» (the owner, 2026-10-12: how many and how good
       * are two questions). The names say a quantity, never a quality.
       */
      countTitle: 'Quants enemics',
      countHelp: 'Quants n’hi ha no canvia com de bons són: això es tria a sota.',
      countNames: {
        '': 'Els del tipus de missió',
        light: 'Pocs',
        medium: 'Uns quants',
        heavy: 'Molts',
      } as Record<string, string>,
      countFromType: (name: string) => `Aquest tipus de missió en posa «${name.toLowerCase()}».`,
      countFromTypeUnknown: 'Els que marca el tipus de missió.',
      countMovedUnder: (players: number, min: number, name: string) =>
        `Sou ${players}, menys dels ${min} que recomana: el generador baixa un grau i en posa «${name.toLowerCase()}».`,
      countMovedOver: (players: number, max: number, name: string) =>
        `Sou ${players}, més dels ${max} que recomana: el generador puja un grau i en posa «${name.toLowerCase()}».`,
      countLess: 'Menys enemics que els que posaria el tipus.',
      countSame: 'Els mateixos que posaria el tipus.',
      countMore: 'Més enemics que els que posaria el tipus.',
      countGroups: (n: number) => `Fins a ${n} grups enemics actius alhora.`,
      countHow: [
        'Quants grups i quants homes hi ha exactament ho escriu el guió de cada tipus de missió (la guarnició, les patrulles, la reacció) a partir d’aquest grau i dels jugadors previstos. La plataforma encara no ho sap dir abans de generar, i per això aquí no hi ha cap xifra d’homes.',
        'Si no en tries cap, el generador fa servir el del tipus i el mou un grau quan sou menys o més jugadors dels que recomana. Si en tries un, és aquest i no es mou.',
        'Els grups actius alhora són els que el joc manté desperts; la resta esperen que us hi acosteu.',
      ],
      /** Card 2, «Com de bons són»: five named stops over the Eden skill number, and the exact value. */
      skillTitle: 'Com de bons són',
      skillHelp: 'El número és l’habilitat de l’Eden. Triar més o menys enemics no el canvia.',
      skillStops: {
        recruits: 'Reclutes',
        regulars: 'Regulars',
        veterans: 'Veterans',
        elite: 'Elit',
        relentless: 'Implacables',
      } as Record<string, string>,
      /** Beside the stop the group's own missions use. */
      skillOwn: 'el del grup',
      skillExact: 'Valor exacte',
      skillRange: (min: string, max: string) => `De ${min} a ${max}.`,
      /** What a level feels like, from what the group's own missions do with it (EnemySkill on the platform). */
      skillFeel: {
        recruits:
          'Per sota de l’enemic de les missions del grup: veu, apunta i reacciona pitjor que l’habitual. A les missions fetes a mà només hi ha uns quants homes així.',
        regulars:
          'El de les missions que el grup fa a mà: la majoria dels seus enemics són a 0,30 a l’Eden.',
        veterans:
          'Un grau per sobre de l’habitual. A les missions del grup és el nivell d’una part dels homes, no de tots.',
        elite:
          'A les missions del grup només el tenen uns quants especialistes. Amb tot l’enemic així, la nit és clarament més dura que les del grup.',
        relentless:
          'Molt per sobre de qualsevol missió del grup. Un enemic massa bo ja va fer una nit «impossible de combatre»: només per a qui ho busca.',
      } as Record<string, string>,
      skillHow: (min: string, max: string) => [
        `El número va de ${min} a ${max} i és el mateix que es posa a l’Eden: els nou atributs de la IA (punteria, tremolor, velocitat d’apuntar, distància i temps de detecció, coratge, comandament, general i recàrrega) queden tots a aquest valor.`,
        'El servidor escala després tots aquests valors amb la seva dificultat (la del grup: skillAI 0,9), igual que a les missions fetes a mà. Per això el valor de referència és el de les missions del grup i no «el del mig».',
        'Abans, sense escriure cap número, el valor sortia de la força triada. Ara són dues tries separades i aquest pas sempre envia el número que mostra.',
      ],
      findBodiesHelp: 'Un enemic despert que veu un dels seus a terra dona l’alarma.',
      /** The rail's line for this step: how many, how good, what is marked. */
      railSkill: (value: string) => `hab. ${value}`,
    },
    /**
     * Step 6: our own forces. The counterpart of the enemy step, and more
     * detailed than it, because what our side is made of is what the lobby
     * shows and what everybody plays.
     */
    ours: {
      title: 'Les nostres forces',
      pickFaction: 'La nostra facció',
      pickFactionHint:
        'Surt del catàleg del servidor. Canvia les classes d’unitat que pots triar més avall.',
      side: 'Bàndol',
      sideHint: 'De quin bàndol juguem. Canvia les faccions que et surten.',
      squadTitle: 'L’esquadra',
      squadSize: 'Places per esquadra',
      squadSizeHint:
        'Cada esquadra és un grup real amb un sol líder. Les places es reparteixen en esquadres d’aquesta mida com a màxim, i mai hi ha dos líders a la mateixa esquadra.',
      squads: (n: number) => `${n} ${n === 1 ? 'esquadra' : 'esquadres'}`,
      rolesTitle: 'Composició de l’esquadra',
      rolesHint:
        'En ordre: la primera fila és la plaça amb què es lidera. Cada esquadra té un sol líder: si hi ha més jugadors que files, se’n fa una altra esquadra, i els rols es reparteixen entre elles.',
      aircrew: (kit: string) =>
        `Si la nit porta helicòpter, el pilot i el copilot són els de la facció: ${kit}.`,
      roleAdd: 'Afegeix una plaça',
      roleRemove: 'Treu la plaça',
      roleUp: 'Amunt',
      roleDown: 'Avall',
      roleFunction: 'Funció',
      roleLabel: 'Nom a la sala',
      roleUnit: 'Classe d’unitat',
      roleUnitDefault: 'La de la facció',
      roleLoadout: 'Equipament',
      roleLoadoutNone: 'El de la unitat',
      roleCount: 'Quantes',
      fromPreset: 'Omple des d’un preset de grup',
      fromPresetNone: 'Tria un preset…',
      reset: 'Torna a la proposta del tipus',
      fromRecipe: 'Proposta del tipus de missió',
      baseTitle: 'D’on sortim',
      base: 'Base de partida',
      baseNone: 'De res: comencem al camp',
      baseHint:
        'Una composició del catàleg construïda al punt d’inserció. La plataforma li busca terreny pla i l’enganxa al punt, així que si el runtime el mou, la base el segueix.',
      baseEmpty:
        'Encara no hi ha composicions al catàleg. Se n’importen des de la pàgina del catàleg.',
      vehiclesTitle: 'Vehicles de partida',
      vehiclesHint:
        'Els trobareu al punt d’inserció, buits. No són un suport: algú els ha de conduir.',
      vehicleAdd: 'Afegeix un vehicle',
      vehicleRemove: 'Treu el vehicle',
      vehicleChange: 'Canvia el vehicle',
      vehicleUnknown: 'Aquest servidor no té aquesta classe',
      vehicleNone: 'Encara no n’hi ha cap. Comencem a peu.',
      noAssets:
        'Els mods d’aquest servidor encara no s’han llegit. Pots triar bàndol i funcions; les classes exactes surten quan s’hagin llegit.',
      noFaction: 'Tria una facció per veure què té.',
      seats: (n: number) => `${n} places`,
      slotRoleNames: {
        teamleader: 'Cap d’esquadra',
        medic: 'Metge',
        rto: 'Ràdio',
        autorifleman: 'Metrallador',
        at: 'Anticarro',
        marksman: 'Tirador',
        engineer: 'Sapador',
        crew: 'Conductor',
        pilot: 'Pilot',
        rifleman: 'Fuseller',
      } as Record<string, string>,
      vehicleKinds: {
        car: 'Lleugers',
        land: 'Terrestres',
        apc: 'Blindats de transport',
        tank: 'Cuirassats',
        heli: 'Helicòpters',
        plane: 'Avions',
        ship: 'Embarcacions',
        static: 'Armes estàtiques',
        other: 'Altres',
      } as Record<string, string>,
    },
    /**
     * The vehicle browser: the whole mod set's rolling stock, with the game's
     * own pictures, one category at a time.
     */
    vehiclePicker: {
      title: 'Tria un vehicle',
      faction: 'Facció',
      allFactions: 'Totes les faccions',
      search: 'Cerca',
      searchPlaceholder: 'Nom o classe…',
      anyKind: 'Totes les categories',
      start: 'Tria una categoria per veure què hi ha, o cerca un vehicle pel nom.',
      empty: 'Cap vehicle amb aquests filtres.',
      more: 'Mostra’n més',
      shown: (n: number, total: number) => `${n} de ${total}`,
      seats: (n: number) => (n === 1 ? '1 plaça' : `${n} places`),
      noSeats: 'Sense places',
      close: 'Tanca',
      loading: 'Carregant…',
      noCatalogue:
        'Els mods d’aquest servidor encara no s’han llegit, així que no hi ha vehicles per triar.',
    },
    /** The faction cards of the enemy step and of ours (FactionPicker.tsx). */
    /** Where a terrain is, for the eras in which that decides who fights (ops.platform.setting.Theatres). */
    theatre: {
      where: 'On és aquest mapa?',
      unknown: 'No ho sé',
      ask: 'En una nit de la Segona Guerra Mundial, el lloc decideix qui hi lluita. Digues-ho un cop i queda desat per a tothom.',
      hint: 'D’aquí surten les faccions que es proposen primer. Si no és correcte, canvia-ho: queda desat per a tothom.',
      names: {
        north_africa: 'Nord d’Àfrica',
        western_europe: 'Europa occidental',
        eastern_front: 'Front oriental',
        pacific: 'Pacífic',
      } as Record<string, string>,
    },
    factionPicker: {
      search: 'Cerca una facció',
      sides: 'Bàndols',
      allSides: 'Tots',
      recent: 'Fetes servir fa poc',
      none: 'Cap facció amb aquest nom.',
      proposed: 'Proposada',
      proposedHint: 'Encaixa amb l’època de la missió i amb el lloc on és el mapa.',
      withoutInfantry: (n: number) =>
        n === 1
          ? '1 facció sense infanteria no s’ofereix com a enemic.'
          : `${n} faccions sense infanteria no s’ofereixen com a enemic.`,
      /** Release D, D5.5: the factions a type does not admit, folded and greyed with the reason. */
      refused: (n: number) => `Altres faccions (no admeses) · ${n}`,
      refusedTitle: 'Altres faccions (no admeses)',
      /** The only irregular faction of the server, shown fixed with the reason (the owner's preference). */
      onlyIrregular:
        'És l’única facció irregular d’aquest servidor, i aquest tipus de missió només es fa contra irregulars.',
    },
    /** How a faction's men look (FactionLooks.tsx), on the enemy step and on ours. */
    looks: {
      ours: 'Com anem',
      oursHint:
        'Els soldats d’aquesta facció, tal com surten a l’editor del joc. La classe de cada plaça es tria a l’esquadra.',
      more: (n: number) => `+${n}`,
      moreTitle: (n: number) => `${n} més amb imatge`,
    },
    /** Step 7, "Sortida": where the night starts, how the players go in and how they come back. */
    start: {
      whereTitle: 'D’on sortim',
      land: 'De terra',
      landBase: 'la base de campanya',
      landHint: 'Una base a terra, de dos a set quilòmetres de l’objectiu.',
      sea: 'Del mar',
      seaBase: 'el grup amfibi',
      seaHint: 'Un vaixell a la costa. D’allà només s’hi entra en helicòpter o en barca.',
      noCoast: 'Aquest terreny no té costa inspeccionada, així que només es pot sortir de terra.',
      jumpHint:
        'En paracaigudes: la base és l’aeròdrom del mapa, amb l’avió a la pista i una caixa de paracaigudes al costat.',
      jumpMore:
        'Qui mana ordena l’enlairament des de l’avió, salteu amb la llum verda sobre la zona de salt i la nit acaba a peu.',
      periodKit: 'Campament d’època de la plataforma (fusta, lona i sacs terrers)',
      landingBase: 'una platja, amb les barcasses a l’aigua',
      seaLandingHint:
        'Un campament en una platja de la costa, amb les barcasses de desembarcament a l’aigua. D’allà només se’n surt en barca.',
      noPiece: (means: string) =>
        `La nostra facció no té res per anar-hi ${means}, ni cap de les seves faccions germanes.`,
      model: 'Model de base',
      noModel: 'Cap model de base al catàleg: la base es munta sense construccions.',
      waysTitle: 'Entrada i sortida',
      in: 'Com hi entrem',
      out: 'Com en tornem',
      back: 'Torneu a la base',
      /** Under each control one short sentence, and the rest of what it says under «Com funciona». */
      howItWorks: 'Com funciona',
      backHint: 'Per defecte la nit acaba on comença, pel mateix mitjà.',
      backMore:
        'Desactiva-ho per triar una sortida diferent, en un altre lloc. En helicòpter, l’helicòpter de transport us recull en una zona d’aterratge marcada al costat del punt d’extracció, a un o dos quilòmetres de l’objectiu, i la nit acaba quan s’enlaira amb tothom a bord. Si allà no hi pot aterrar cap helicòpter, la sortida és a peu fins al mateix punt i la missió us diu per què.',
      folder: 'Les ordres es recullen a la taula de briefing (carpeta)',
      folderHint:
        'Fins que algú les agafi, el mapa no mostra els objectius; passats cinc minuts la missió comença igualment.',
      folderMore: 'Per defecte les ordres es coneixen des del briefing.',
      who: 'Qui ho decideix',
      whoCreator: 'El creador',
      whoVote: 'Votació abans',
      whoHint: 'Amb una sola opció no hi ha res a decidir.',
      whoMore:
        'Amb més, o bé ho tria el creador abans de començar, o bé es vota abans de la sessió.',
      vehicles: 'Vehicles a la base',
      vehiclesHint: 'Surten del mitjà triat, de la nostra facció, i els pots canviar.',
      vehiclesMore: 'Si una opció es vota, hi ha els de totes.',
      vehiclesNone: 'Cap: a peu no en cal cap.',
      vehiclesFromBase: 'Els que porta la base, on el grup els aparca.',
      vehiclesFromBaseMore:
        'Pots canviar-los o afegir-ne; el que afegeixis es posa on quedi lloc a la coberta.',
      noTransport: (means: string) =>
        `La nostra facció no té res per anar ${means}: afegeix-ne un a mà si cal.`,
      /** The seats against the players, in «Vehicles a la base» (the owner, 2026-10-19). */
      seatsBy: (means: string, seats: number, players: number) =>
        `${means.charAt(0).toUpperCase()}${means.slice(1)}: ${seats === 1 ? '1 seient' : `${seats} seients`} per a ${players === 1 ? '1 jugador' : `${players} jugadors`}.`,
      seatsTotal: (seats: number, players: number) =>
        `${seats === 1 ? '1 seient' : `${seats} seients`} en total per a ${players === 1 ? '1 jugador' : `${players} jugadors`}.`,
      seatsShort: (n: number) =>
        n === 1
          ? 'En falta 1: tothom ha de tenir seient.'
          : `En falten ${n}: tothom ha de tenir seient.`,
      seatsFill: 'Afegeix-ne fins que hi càpiguen tots',
      seatsNoRoom:
        'A la base no hi caben més vehicles d’aquest tipus: canvia’n algun per un de més gran.',
      sentence: (base: string, inText: string, outText: string) =>
        `Sortiu de ${base} ${inText}; torneu ${outText}.`,
      sentenceBack: (base: string, inText: string) =>
        `Sortiu de ${base} ${inText}; hi torneu de la mateixa manera.`,
      point: 'D’un punt concret',
      pointBase: 'la base on la posis',
      pointHint:
        'Tria al mapa una instal·lació, un aeròdrom o qualsevol punt de terra ferma; la base es munta allà, sigui quina sigui la distància.',
      pointPick: 'Tria el punt de sortida al mapa.',
      pointChosen: (name: string | null, x: number, y: number) =>
        name ? `Sortiu de ${name} (${x}, ${y}).` : `Sortiu del punt ${x}, ${y}.`,
      pointNoObjective: 'L’objectiu encara no està triat: es tria en generar.',
      pointNoObjectiveMore: 'Torna al pas del mapa i tria un lloc per veure’l aquí.',
      landShort: 'Terra',
      seaShort: 'Mar',
      pointShort: 'Punt',
      voted: (text: string) => `${text} (es vota)`,
      creatorLater: (text: string) => `${text} (ho tria el creador)`,
      phrases: {
        foot: 'a peu',
        vehicle: 'en vehicle',
        helicopter: 'en helicòpter',
        boat: 'en barca',
        parachute: 'en paracaigudes',
        deployed: 'des de les posicions',
      } as Record<string, string>,
    },
    /** The picture a mission is shown with (the designer's Configuració panel). */
    cover: {
      title: 'Imatge de portada',
      hint: 'És la imatge que surt a la biblioteca, a la portada i a la llista de sessions. Cap imatge no es puja: la plataforma dibuixa el que ja sap de la missió.',
      AUTO: 'Automàtica',
      AUTOHint: 'La imatge del terreny si n’hi ha; si no, el mosaic del tipus de missió.',
      TERRAIN: 'El terreny',
      TERRAINHint:
        'Sempre la imatge del mapa: la del panell, la del Workshop o el relleu del sondeig.',
      TYPE: 'El tipus de missió',
      TYPEHint: 'Sempre el mosaic dibuixat amb la icona i el color del tipus.',
      noType: 'Missió',
    },
    /** The details panel every picker opens once something is chosen. */
    /** The survey's map of one type's choices on one terrain (the map and start steps). */
    candidates: {
      loading: 'Buscant els llocs possibles al survey…',
      unavailable:
        'Aquest terreny encara no s’ha inspeccionat: el mapa de llocs possibles no es pot dibuixar.',
      objective: 'Objectiu',
      objectivesCount: (n: number, needs: string | null) =>
        // A type built on a height or an airfield draws only those, never the
        // towns: said, so a short list does not read as a map half read (web
        // card 196, 2026-10-18: «Estació de radar» on Altis, fifteen heights).
        needs && needs !== 'settlement'
          ? `${n} ${n === 1 ? 'lloc possible' : 'llocs possibles'} per a aquest tipus, que necessita ${groundName(needs)}: el mapa només mostra aquests llocs, no els pobles.`
          : `${n} ${n === 1 ? 'nucli possible' : 'nuclis possibles'} per a aquest tipus. Com més gran és el punt, més gran és el nucli.`,
      basesCount: (reach: number, total: number) =>
        `${total} ${total === 1 ? 'base possible' : 'bases possibles'}, ${reach} a l’abast de l’objectiu.`,
      clickObjective: 'Clica un lloc per muntar-hi la missió.',
      clickBase: 'Clica una base, o qualsevol punt de terra, per sortir-ne.',
      farFromObjective: 'fora de l’abast',
      baseKind: (kind: string) =>
        (
          ({
            site: 'Instal·lació',
            airfield: 'Aeròdrom',
            camp: 'Campament',
            ship: 'Vaixell',
          }) as Record<string, string>
        )[kind] ?? kind,
      mapTitle: 'Llocs possibles',
      mapHint:
        'On el generador podria muntar la missió d’aquest tipus, segons el survey del terreny.',
    },
    details: {
      title: 'Detalls',
      pickFirst: 'Tria un element per veure’n els detalls.',
      presetMods: (n: number) => (n === 1 ? '1 mod' : `${n} mods`),
      supportsSystemLabel: 'Suports',
      terrainSize: 'Mida',
      terrainSource: 'Origen',
      terrainWorkshop: 'Workshop',
      terrainUnknownSize: 'sense mesurar',
      aroundPlace: 'Munta la missió al voltant de',
      aroundPlaceAny: 'Qualsevol lloc (a l’atzar)',
      aroundPlaceHint:
        'Els objectius es col·loquen en aquest nucli i al seu voltant. Clica un lloc al mapa per triar-lo.',
      noPlaces: 'Aquest mapa no té llocs indexats; cal llegir el contingut dels mods del servidor.',
      enemySide: 'Bàndol',
      enemyClass: 'Facció',
      enemyDefences: 'Defenses de posició',
      enemyDefencesNone: 'Cap: terreny pelat',
      enemyDefencesHint:
        'Una composició del catàleg construïda a cada objectiu que l’enemic manté, orientada cap on venim: sacs terrers, filferro, els trastos d’un control. No surt al mapa dels jugadors, que és mig sentit de posar-n’hi.',
      /** Whether an enemy who sees one of his own dead reacts (the owner, 2026-10-11); on by default. */
      enemiesFindBodies: 'Els enemics troben els seus morts',
      enemiesFindBodiesHint:
        'Un enemic despert que veu un dels seus a terra s’atura, crida, s’hi acosta i mira; després el seu grup es posa en alerta i el cap n’informa per ràdio. De nit hi veuen de més a prop. Un cos amagat no el troba ningú. Desactivar-ho no toca les missions que ja tenen la seva pròpia regla de cossos, com la incursió nocturna, on també es continuen podent amagar.',
      /** Whose cars the enemy's men ride when a mounted force is asked (2026-10-10). */
      enemyRides: 'Vehicles',
      enemyRidesOwn: 'Propis',
      enemyRidesGroups: (faction: string) => `De ${faction}, que els seus grups anomenen`,
      enemyRidesDonor: (faction: string) => `De ${faction}, el donant declarat`,
      enemyRidesNone: 'Cap: les forces muntades van a peu',
      enemyLooks: 'Quin aspecte té',
      enemyLooksHint: "Els soldats d'aquesta facció, tal com surten a l'editor del joc.",
      enemyLooksNone: 'Aquesta facció no té cap soldat al catàleg del servidor.',
      enemyLooksNoPictures:
        'Aquest mod no publica cap imatge dels seus soldats: només en tenim el nom.',
    },
    /** Why a terrain cannot host a mission type: it has none of the ground it needs. */
    needsGround: (kind: string) => {
      const names: Record<string, string> = {
        airfield: 'un aeròdrom',
        site: 'una instal·lació militar',
        bridge: 'un pont',
        road: 'carreteres',
        settlement: 'poblacions',
        high_ground: 'terreny elevat',
        water: 'costa',
        flat: 'terreny pla',
      };
      return `Aquest mapa no té ${names[kind] ?? kind}: aquest tipus de missió hi necessita.`;
    },
    /** The ground a type needs, said on its card before any terrain is chosen. */
    needsGroundHint: (kind: string) => `Necessita ${groundName(kind)}`,
    /** A terrain that lacks what the chosen type needs, on the map step. */
    lacksGround: (kind: string) => `No té ${missingGround(kind)}`,
    refusedGround: (detail: string) => {
      const [kind, extra] = detail.split(':');
      return `Aquest mapa no té ${missingGround(kind)}${extra === 'no_survey' ? ' (el survey no està acabat)' : ''}: tria un altre mapa o un altre tipus de missió.`;
    },
    /**
     * night_raid's own camp and reaction-post ground (`no_camp_ground`,
     * `no_qrf_post`): the same terrain-needs-a-survey case as `refusedGround`,
     * but the reason is the kind's own full sentence ("camp:no_survey — …"),
     * not a bare place kind, so it only needs the `:no_survey` marker.
     * Retrying changes nothing: the terrain needs a survey run on it first.
     */
    needsSurvey:
      'Aquest terreny encara no té cap escaneig (survey) complet: cal escanejar-lo abans de poder-hi generar cap missió. Tornar-ho a provar no ho soluciona.',
    invitedEditorsTitle: 'Editors convidats',
    editors: {
      title: 'Editors convidats',
      hintDraft:
        'Els que triïs podran editar la missió quan estigui feta. Cal que ja tinguin un compte amb permís d’editar.',
      hintLive: 'Qui pot canviar aquesta missió a part dels administradors.',
      field: 'Convida algú pel nom o pel nom d’usuari',
      none: 'Encara no has convidat ningú.',
      loading: 'Carregant els editors…',
      author: 'autoria',
      remove: (name: string) => `Treu ${name} dels editors`,
      loadFailed: 'No s’ha pogut carregar la llista d’editors.',
      inviteFailed: 'No s’ha pogut convidar. Torna-ho a provar.',
      inviteRefused:
        'Només es pot convidar algú que ja tingui un compte amb permís d’editar missions.',
      removeFailed: 'No s’ha pogut treure l’editor. Torna-ho a provar.',
      notInvited: (names: string) =>
        `La missió s’ha fet, però no s’ha pogut convidar: ${names}. Pots tornar-ho a provar des de la pàgina de la missió.`,
    },
    xmlImport: 'Importa llista del Workshop (.xml)',
    terrain: 'Mapa',
    enemy: 'Enemic',
    searchTerrain: 'Cerca un mapa',
    searchType: 'Cerca un tipus de missió',
    searchTypeClear: 'Esborra la cerca',
    searchTypeNone: (q: string) => `Cap tipus de missió no coincideix amb «${q}».`,
    noTerrains: 'Cap mapa amb aquest nom.',
    changeTerrain: 'Canvia el mapa',
    chosenTerrain: 'Mapa triat',
    noDraft:
      'Aquesta pestanya no té cap missió a mig fer. L’assistent comença pel servidor on es jugarà: d’allà surten els mods, els mapes i les faccions.',
    playersRange: (min: number, max: number) => `${min}–${max}`,
    playersRangeTitle: 'Jugadors',
    duration: (min: number) => `~${min} min`,
    durationTitle: 'Durada habitual',
    strengthTitle: 'Enemic',
    strengthNames: { light: 'lleuger', medium: 'mitjà', heavy: 'fort' } as Record<string, string>,
    comingSoon: 'Properament',
    /** Beside «Properament» when an admin may still pick it, as the platform lets an admin generate it. */
    adminsOnly: '(només administradors)',
    comingSoonHint: 'Encara no està a punt: surt aquí perquè es vegi què vindrà.',
    /** A recipe scripted and implemented, not accepted yet (RecipeStatus.TESTING). */
    testing: 'En proves',
    /** With the test cards it has passed and the ones it needs before it is proposed as ready. */
    testingProgress: (passed: number, required: number) => `En proves · ${passed} de ${required}`,
    testingHint: 'La recepta és nova i encara l’estem afinant.',
    sizeAndTime: 'Mida i moment',
    /** Step 5: the optional objectives a type may carry (QUICK_START_REVIEW.md F6). */
    secondaries: {
      title: 'Objectius secundaris',
      who: 'Qui els decideix',
      bySeed: 'La llavor',
      these: 'Aquests',
      vote: 'Votació',
      hintSeed: 'La llavor en pren cadascun segons la seva probabilitat.',
      hintThese:
        'Només els marcats, tinguin la probabilitat que tinguin. Cap de marcat és una nit sense secundaris.',
      hintVote: 'Els marcats es voten abans de la sessió.',
      chanceTitle: 'Probabilitat que la llavor el prengui',
      found: 'S’obre amb una troballa: els jugadors el descobreixen sobre el terreny',
      none: 'Aquest tipus de missió no en té.',
      rowSeed: 'La llavor',
      rowChosen: (n: number) => (n === 0 ? 'Cap' : n === 1 ? '1 triat' : `${n} triats`),
      rowVote: (n: number) => `Votació abans · ${n}`,
    },
    railPlayers: (n: number) => `${n} jugadors`,
    /** Under the players field, once a type is chosen: how many people it is written for. */
    playersRangeHint: (min: number, max: number) =>
      `Aquest tipus de missió és per a ${min}–${max} jugadors.`,
    /**
     * The party outside the type's range: a warning, never a wall (the owner,
     * 2026-09-28). The generator scales the enemy and notes it in the mission.
     */
    playersOutOfRange: (min: number, max: number) =>
      `Aquest tipus de missió està pensat per a ${min}–${max} jugadors. Podeu continuar: la missió es generarà igualment, però pot quedar desequilibrada.`,
    /** A15: shown under the hour field for a night-only type (Recipe.NIGHT_ONLY). */
    nightOnlyHint: 'Aquest tipus de missió només es pot fer de nit.',
    /**
     * Where a refusal is fixed (ENGINE_DESIGN.md §2.20, `fixBy`): said under a
     * greyed type, map or refusal, so the reason comes with what to do about it.
     */
    fixBy: {
      server: 'Es resol triant un altre servidor.',
      type: 'Es resol triant un altre tipus de missió.',
      map: 'Es resol triant un altre mapa.',
      enemy: 'Es resol triant un altre enemic.',
      ours: 'Es resol triant una altra facció nostra.',
      start: 'Es resol al pas «Sortida».',
      decide: 'Es resol al pas «Decideix».',
      scan: 'Es resol escanejant el mapa des del catàleg.',
    } as Record<string, string>,
    /**
     * The request parameter a refusal is about (`param_not_admitted`'s `param`,
     * release D): named before the sentence, so the creator knows which field.
     */
    paramName: (param: string) =>
      (
        ({
          players: 'Jugadors',
          hour: 'Hora',
          weather: 'Temps',
          insertion: 'Entrada',
          extraction: 'Sortida',
          startFrom: 'Base de partida',
          supports: 'Suports',
          twists: 'Girs',
          secondaryObjectives: 'Objectius secundaris',
          enemyFaction: 'Enemic',
          startComposition: 'Base de partida',
          playerPilots: 'Pilots',
        }) as Record<string, string>
      )[param] ?? (param.startsWith('settings.') ? 'Opcions del tipus' : param),
    /** What a type will play instead where it admits it, said on its card rather than silently. */
    fallbacksTitle: 'Si cal, farà',
    /** The hour field's default for a type that works its own hour out (night_raid's, from the dawn). */
    hourFromType: '— la que calcula el tipus —',
    /** A secondary objective that belongs to this type alone. */
    ownOptional: 'propi del tipus',
    /** Under the ways out, when the type decides where the night ends (R2). */
    wayOutFixed: 'La sortida la decideix el tipus de missió',
    /** Under a twist, when the type only lets it happen in part of the night. */
    twistWindow: (words: string) => `Només ${words}.`,
    /** On the last step, when what was chosen since the type cannot play it. */
    notPlayable: 'Amb el que s’ha triat, aquest tipus de missió no es pot jugar',
    /**
     * The link under a refusal to the step that fixes it (ENGINE_DESIGN.md §2.20,
     * `fixBy`); `decide` and `none` have none, this step is where the person is.
     */
    fixByLink: {
      server: 'Canvia el servidor',
      type: 'Canvia el tipus de missió',
      map: 'Canvia el mapa',
      enemy: 'Canvia l’enemic',
      ours: 'Canvia les nostres forces',
      start: 'Ves al pas «Sortida»',
      scan: 'Obre el mapa al catàleg per escanejar-lo',
    } as Record<string, string>,
    /** What the last step says of an hour kept from before that the type does not admit (R1). */
    typeDecides: {
      hourReplaced: (asked: string, reason: string) =>
        `Vau demanar les ${asked}, fora de la finestra del tipus: la substitueix. ${reason}`,
    },
    /**
     * The dialog a generation refusal opens (the owner, 2026-10-11: «el missatge de
     * rebuig hauria de ser un popup, no un text mig amagat a la pàgina»): the
     * sentence alone, what the platform worked out would work as buttons, and the
     * technical detail behind a disclosure, never on a member's screen unasked.
     */
    refusal: {
      title: 'No s’ha pogut crear la missió',
      whatNow: 'Què pots fer:',
      changeWayIn: (means: string) =>
        `Canvia l’entrada a ${
          (
            {
              helicopter: 'helicòpter',
              vehicle: 'vehicle',
              foot: 'a peu',
              boat: 'barca',
            } as Record<string, string>
          )[means] ?? means
        }`,
      changeObjectiveTo: (name: string) => `Canvia l’objectiu a ${name}`,
      otherObjective: 'Tria un altre objectiu',
      pickStartPoint: 'Tria el punt de sortida al mapa',
      technical: 'Detall tècnic',
      close: 'Tanca',
    },
    /** Step 8: a mission that generated but could not do everything as asked. */
    generated: {
      title: 'La missió s’ha generat, amb avisos',
      titleOnly: 'La missió s’ha generat',
      hint: 'El generador no ho ha pogut fer tot tal com es demanava i ho diu aquí. També ho trobareu a la pàgina de la missió.',
      openMission: 'Obre la missió',
      openSession: 'Obre la sessió',
    },
    /**
     * The last step, one column (the owner, 2026-10-12): title, when, hour and
     * weather, supports, twists, the votes folded, a summary and the button.
     * Each control is free, limited or fixed by what the type declares.
     */
    decide: {
      title: 'Títol',
      titlePlaceholder: 'el proposa la plataforma',
      titleHelp: 'Opcional: sense títol, la plataforma en proposa un amb el tipus i el lloc.',
      skyTitle: 'Hora del dia i temps',
      /** A lock's own name: the value beside it is the type's, not the creator's. */
      fixedByType: 'Ho fixa el tipus de missió',
      hourLimited: (words: string) => `Aquest tipus de missió es juga ${words}.`,
      hourFixed: (words: string) => `Es juga ${words}`,
      weatherLimited: (words: string) => `Aquest tipus de missió només admet ${words}.`,
      supportsLimited: (words: string) => `Aquest tipus de missió només admet ${words}.`,
      supportsNone: 'Aquest tipus de missió no admet cap suport dels que es poden demanar.',
      twistsLimited: 'Només es mostren els girs que aquest tipus de missió admet.',
      twistsNone: 'Cap: aquest tipus de missió no admet girs.',
      /** While a row is put to a vote or left to the seed, its value is not the creator's to set. */
      notCreators: (who: string) => `${who}: es canvia a «Votacions».`,
      clock: 'Rellotge',
      votesTitle: 'Votacions (opcional)',
      votesHelp:
        'Sense tocar res, cada cosa la decideix qui crea la missió. Aquí es pot deixar a una votació del grup o a l’atzar.',
      votesFixed: (names: string) =>
        `No es poden votar perquè les fixa el tipus de missió: ${names}.`,
      summaryTitle: 'Resum',
      contra: (enemy: string) => `contra ${enemy}`,
      supports: (n: number) => (n === 0 ? 'sense suports' : n === 1 ? '1 suport' : `${n} suports`),
      hourOf: (h: string) => `a les ${h}`,
      hourFromType: 'hora del tipus',
      hourSeeded: 'hora a l’atzar',
      weatherSeeded: 'temps a l’atzar',
      create: 'Crea la missió',
      creating: 'Creant la missió…',
    },
    decisionTable: {
      twistByHand: 'Els girs, els pot avançar qui dirigeix (si no, passen sols)',
      row: 'Decisió',
      value: 'Valor actual',
      decidedBy: 'Ho decideix',
      map: 'Mapa',
      enemy: 'Enemic',
      type: 'Tipus de missió',
      nextChapter: 'Capítol següent',
      byChance: 'Ho decidirà l’atzar',
      byVote: 'Ho decidirà una votació',
      changeAtStart: 'Es canvia a «Sortida»',
      commanderAtSession: 'Es tria a la sessió: el nomena la direcció o el vota el grup.',
      changeAtType: 'Es canvia a «Tipus»',
      changeAtServer: 'Es canvia a «Servidor»',
    },
    visibility: {
      title: 'Qui la veu',
      group: 'Tots els membres',
      private: 'Només jo',
      groupHint:
        'Qualsevol membre pot veure la missió i les seves nits. Ho pots canviar des de la pàgina de la missió.',
      privateHint:
        'Només la veus tu, qui l’edita amb tu i qui juga la nit; l’informe i la sessió no surten per a ningú més, ni s’anuncien a Discord.',
    },
    when: {
      title: 'Quan es juga',
      now: 'Ara mateix',
      dateTime: 'Data i hora',
      onStart: 'En arrancar la sessió',
      onStartHint: 'Es crea la sessió sense data i s’arrenca a mà quan vulguis.',
      server: 'Servidor',
      noServer: '— cap —',
      serverOff: 'aturat',
      serverLacksTerrain:
        'Aquest servidor no té el mod del mapa triat: canvia el servidor o el mapa abans de generar.',
      serverOtherMods: 'mods diferents',
      serverSameMods: 'mateixos mods',
      serversUnlinked: 'Cal enllaçar-los',
      serversUnlinkedHint:
        'Els servidors del grup que la plataforma encara no pot fer servir surten en gris.',
      serversLinkThem: 'Enllaça’ls a Servidors',
      serverNoCandidate:
        'Cap servidor que la plataforma pugui fer servir no té el terreny d’aquesta missió. Pots crear-ne un per a aquesta sessió.',
      serverNoneMatching:
        'Cap servidor porta els mods d’aquest preset. Pots crear-ne un per a aquesta sessió.',
      serverCreate: 'Crea un servidor per a aquesta sessió',
      serverCreating: 'Creant el servidor…',
      serverCreated: (name: string) => `Servidor «${name}» creat i seleccionat.`,
    },
    /**
     * Which fire-support system the night is played with. Neither counts
     * anything (the owner, 2026-09-26): a support is there or it is not, and a
     * cooldown between calls is what holds it back on either path, so the
     * card asks for a tick and never a number.
     */
    supportsSystem: {
      noPieceShort: 'no n’hi ha',
      /** A support of the catalogue this type does not admit: shown padlocked with this reason. */
      notAdmitted: 'aquest tipus de missió no l’admet',
      /** A support the type always brings (release D, D5.9). */
      fixed: 'el porta sempre aquest tipus de missió',
      /** A support the type's own entry limits to a count (release D, D5.9). */
      limited: (name: string, n: number) =>
        `${name}: ${n} ${n === 1 ? 'missió' : 'missions'} (pròpies d’aquest tipus de missió)`,
      /** The names of the supports a type brings of its own, by entry. */
      ownNames: {
        navalFire: 'Canó del vaixell',
        brigadeFire: 'Foc de la brigada',
      } as Record<string, string>,
      noPiece:
        'Un suport s’ofereix només quan la nostra facció, o les seves faccions germanes, té una peça per fer-lo; el dron, només en una nit moderna; i sense Simplex al servidor, tots menys l’evacuació mèdica.',
      /** Why a support is off, by the reason the server gave (SupportOffer). */
      why: {
        era: 'no n’hi ha en aquesta època',
        no_carrier: 'cal Simplex al servidor',
        no_piece: 'la facció no té res per fer-lo',
      } as Record<string, string>,
      simplex: 'Simplex Support Services',
      vanilla: 'El menú de suports del joc',
      simplexHint:
        'Aquest servidor porta Simplex Support Services, que és el que fa servir el grup. Els suports es demanen des del seu menú i no s’esgoten: quan passa la recàrrega, tornen a respondre. Per això aquí es marquen, no es compten.',
      vanillaHint:
        'Sense Simplex, els suports es demanen amb el menú de comunicacions del joc (0-8), assenyalant amb el cursor on han d’anar, i tampoc no s’esgoten: entre crida i crida hi ha un temps de recàrrega. Per això aquí es marquen, no es compten.',
      noAsset: 'El catàleg del servidor no té cap peça per a aquest suport; anirà al menú del joc.',
    },
    /** The turns the night takes, chosen at creation instead of by the seed. */
    /**
     * A type's own settings (release D, D6.3): its switches, shown with the twists
     * as optional twists that are off unless ticked, and its choices, apart.
     */
    settings: {
      switchChip: 'gir opcional · desactivat per defecte',
      switchedOn: (names: string) => `Activats: ${names}.`,
      choicesTitle: 'Opcions d’aquest tipus de missió',
      choicesHint: 'Cada opció té el seu valor per defecte; el generador no en sorteja cap.',
      byDefault: '(per defecte)',
    },
    twists: {
      title: 'Girs',
      hint: 'Marca els que vulguis. Sense marcar-ne cap, la nit no té cap gir.',
      chosen: (n: number) => (n === 1 ? '1 gir triat' : `${n} girs triats`),
      none: 'Cap gir',
      chance: (pct: number) => `${pct}% per llavor`,
      // The stand-ins the preview puts in a twist's template while no town is
      // chosen. Each has to read in every template it can land in: "la població"
      // gave "Hi ha ferits entre la població de la població" (F14).
      somePlace: 'la zona',
      otherPlace: 'un nucli proper',
      someone: 'l’objectiu',
    },
    openDesigner: 'Obre el dissenyador complet',
    page: {
      sides: 'Bàndols',
      ours: 'Nosaltres',
      theirs: 'Enemic',
      warningsTitle: 'El generador no ha pogut fer-ho tal com es demanava',
      warning: (code: string) => {
        const [kind, what, extra] = code.split(':');
        if (kind === 'no_place_of_kind') {
          const names: Record<string, string> = {
            site: 'cap instal·lació militar',
            airfield: 'cap aeròdrom',
            bridge: 'cap pont',
            road: 'cap carretera',
          };
          return `Aquest terreny no té ${names[what] ?? what} conegut${extra === 'no_survey' ? ' (el survey no està acabat)' : ''}: l’objectiu s’ha posat en un nucli habitat.`;
        }
        if (kind === 'supports_without_piece') {
          const supports: Record<string, string> = {
            artillery: 'artilleria',
            cas: 'suport aeri',
            transport: 'transport',
            supplyDrop: 'llançament de subministraments',
            uav: 'UAV',
            medevac: 'evacuació mèdica',
          };
          const names = (what ?? '').split(',').map((k) => supports[k] ?? k);
          return `La nostra facció no té cap peça per a: ${names.join(', ')}. La missió s’ha fet sense.`;
        }
        if (kind === 'supports_without_carrier') {
          const supports: Record<string, string> = {
            cas: 'suport aeri',
            transport: 'transport',
            uav: 'dron',
            medevac: 'evacuació mèdica',
          };
          const names = (what ?? '').split(',').map((k) => supports[k] ?? k);
          return `Aquest servidor no porta Simplex Support Services i sense no es poden demanar: ${names.join(', ')}. La missió s’ha fet sense.`;
        }
        if (kind === 'fallback') {
          const fallbacks: Record<string, string> = {
            reaction_on_foot:
              'L’enemic no té cap vehicle on hi càpiga la secció de reacció: vindrà a peu, encara que el seu lloc sigui lluny.',
            column_trucks:
              'La nostra facció no té camions propis al catàleg: el comboi porta els que el joc li ha trobat.',
            escort_cars:
              'La nostra facció no té cap cotxe adequat per fer d’escorta (només camions i vehicles de servei) i el conjunt de mods no té cap cotxe civil: la comitiva és només el vehicle del principal.',
            vehicle_uses_by_name:
              'El catàleg d’aquest conjunt de mods es va escanejar abans que el joc ens digués per a què serveix cada vehicle: els cotxes de la comitiva s’han triat pel nom i algun pot ser un vehicle de servei. Torna a escanejar el conjunt de mods.',
            base_roads_unknown:
              'Aquest mapa es va inspeccionar abans que sabéssim per on passen tots els camins: la base s’ha buscat en terreny pla, i si cau damunt d’un camí serà la partida mateixa qui la mourà. Torna a inspeccionar el mapa per saber-ho abans.',
            motorcade_civilian:
              'La nostra facció no té cotxes propis (cap, o només camions i vehicles de servei): el principal i l’escorta van en cotxes civils, com una comitiva discreta.',
            // fallback:<recipe>:no_<kind> (the people recipes' place kinds, 2026-10-11).
            capture_hvt:
              'La inspecció d’aquest terreny no hi troba cap casa adequada per a la trobada: l’oficial serà a l’edifici més gran a prop del nucli triat.',
            rescue_hostage:
              'La inspecció d’aquest terreny no hi troba cap casa adequada per tenir-hi els ostatges: seran a l’edifici més proper al nucli triat.',
            retrieve_intel:
              'La inspecció d’aquest terreny no hi troba cap edifici adequat per a l’oficina: la informació serà a l’edifici més proper al nucli triat.',
            csar: 'La inspecció d’aquest terreny no hi troba cap terreny obert amb un bosc a prop on hagi pogut caure l’aparell: caurà a tocar del nucli triat.',
          };
          if (what === 'clear_area_generic') {
            // fallback:clear_area_generic:<why> (ClearAreaScript.genericInstead): the village's script is
            // not written and the generic clearance is generated in its place.
            const why: Record<string, string> = {
              no_survey: 'aquest terreny encara no té cap inspecció acabada',
              needs_resurvey:
                'la inspecció d’aquest terreny és anterior als edificis i els carrers i cal repetir-la',
              no_irregular_faction:
                'el conjunt de mods no té cap facció irregular al bàndol enemic',
            };
            return `La neteja s’ha fet sense sectors ni reducte, com una neteja d’àrea corrent: ${why[extra] ?? extra ?? 'no s’hi ha pogut escriure el guió del poble'}.`;
          }
          return fallbacks[what] ?? code;
        }
        if (kind === 'clear_area_district') {
          // clear_area_district:<town>:<metres>:<buildings> (ClearAreaScript): a town asked for by name is never
          // cleared whole; the night is a district round its centre.
          const [, , metres, buildings] = code.split(':');
          return `${what} és massa gran per netejar-la sencera: la missió és només el centre, un barri d’uns ${metres} m al voltant amb ${buildings} edificis on es pot combatre. La resta de la ciutat no és objectiu.`;
        }
        if (kind === 'preferred_place_not_used') {
          // preferred_place_not_used:<name> (MissionGenerator.preferred): the place asked for is not one an
          // objective can stand on, and the seed chose another.
          return `El lloc que es va demanar, ${what}, no és cap nucli d’aquest terreny on es pugui posar l’objectiu (o és a l’aigua): se n’ha triat un altre.`;
        }
        if (kind === 'hour_replaced') {
          const clock = (h: string | undefined) => {
            const n = Number(h);
            if (!Number.isFinite(n)) return h ?? '?';
            const m = Math.round(n * 60) % (24 * 60);
            return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
          };
          return `Es va demanar començar a les ${clock(what)}; aquest tipus de missió no ho admet i comença a les ${clock(extra)}.`;
        }
        if (kind === 'short_runway') {
          // short_runway:<metres> (Jump.RUN_NEEDED): the only strip in reach is under what a take-off is given.
          return `La pista d’enlairament fa ${what} m, menys dels 700 que un enlairament té assignats: l’avió s’hi enlaira igualment, escurçant la carrera.`;
        }
        if (kind === 'players_outside_recommended') {
          // players_outside_recommended:<min>-<max> (engine Constraints.PLAYERS_OUTSIDE): the party's own size is not in it.
          const [min, max] = (what ?? '').split('-');
          return `Aquest tipus de missió està pensat per a ${min}–${max} jugadors; s’ha fet igualment per al vostre grup, i l’enemic s’hi ha ajustat.`;
        }
        if (kind === 'date_moved_for_dark_moon') {
          return `La data s’ha mogut al ${what}${extra ? `:${extra}` : ''} perquè aquest tipus de missió vol una nit sense lluna.`;
        }
        if (kind === 'weather_cleared_no_night_vision') {
          const asked: Record<string, string> = {
            fog: 'boira',
            cloudy: 'cel ennuvolat',
            rain: 'pluja',
            storm: 'tempesta',
          };
          return `Es va demanar ${asked[what] ?? what}, però els jugadors no porten visió nocturna: la nit té el cel serè perquè la lluna s’hi vegi.`;
        }
        if (kind === 'night_visibility_unmet') {
          const why: Record<string, string> = {
            date: 'la data que es va demanar',
            hour: 'l’hora que es va demanar',
            date_and_hour: 'la data i l’hora que es van demanar',
            no_high_moon: 'aquest terreny i aquesta època de l’any',
          };
          return `Els jugadors no porten visió nocturna i la nit hauria de tenir lluna plena i alta, però amb ${why[what] ?? what} no s’hi arriba: serà més fosca.`;
        }
        if (kind === 'base_on_rough_ground') {
          const [, , span, degrees] = code.split(':');
          const slope = `un pendent d’uns ${degrees}° (${span} m de desnivell d’un extrem a l’altre)`;
          return what === 'rough'
            ? `La base és en terreny irregular: a prop d’aquest objectiu no hi ha cap pla on hi càpiga una base, i el menys dolent que hi ha és ${slope}. S’hi munta només el campament mínim, sense murs; alguna tenda o algun vehicle pot quedar inclinat o no aparèixer.`
            : `La base és en terreny irregular: a prop d’aquest objectiu no hi ha cap pla on hi càpiga una base, i s’hi munta només el campament mínim, sense murs, en ${slope}.`;
        }
        if (kind === 'base_band_widened') {
          const km = (Number(what) / 1000).toFixed(1).replace('.', ',');
          return `La base és a ${km} km de l’objectiu, més lluny del que és habitual per a aquesta entrada: més a prop no hi ha terreny pla on hi càpiga.`;
        }
        if (kind === 'base_near_edge') {
          return `La base és a ${what} m de la vora del mapa: és l’únic terreny pla a l’abast d’aquest objectiu.`;
        }
        if (kind === 'objective_moved_to_dry') {
          // objective_moved_to_dry:<metres> (MissionGenerator.dryJitter): the place's own ground and everything round it
          // within 600 m is water; the objective stands on the nearest dry ground instead.
          return `L’objectiu havia de caure a l’aigua, perquè al voltant del lloc triat no hi ha terra ferma: s’ha mogut ${what} m fins a la terra seca més propera, i el que s’explica del lloc pot no coincidir amb el que hi veureu.`;
        }
        if (kind === 'composition_none_enabled') {
          // composition_none_enabled:<era> (MissionService.campFor): the library has start bases for the night, none enabled.
          return `No hi ha cap base de partida activa per a aquesta època i aquest teatre: s’ha muntat el campament de la plataforma.`;
        }
        if (kind === 'composition_none_fits') {
          // composition_none_fits:<library key> (MissionService.campFor): no enabled base fits whole; the smallest stands.
          return `Cap base de partida activa no hi cabia sencera: s’ha muntat la més petita.`;
        }
        if (kind === 'base_no_helipad_room') {
          // base_no_helipad_room (MissionService.campFor): the night has helicopters, no start base of the library has a pad of
          // its own or room for one beside it. The runtime still stands each aircraft on open ground it finds clear (up to 500 m).
          return `No hi havia terreny pla i lliure per a un heliport al costat de la base: els helicòpters es posen més lluny, on el terreny ho permet.`;
        }
        if (kind === 'base_vehicles_reduced') {
          return `A la base hi ha ${what} dels ${extra} vehicles de terra demanats: en terreny irregular només s’aparca als llocs marcats del campament.`;
        }
        if (kind === 'way_out_far') {
          // way_out_far:<means>:<metres from the objective>:<ceiling>:<ceiling|beyond_base> (generator WayOut).
          const metres = extra;
          const why = code.split(':')[4];
          const how: Record<string, string> = {
            foot: 'a peu',
            vehicle: 'en vehicle',
            helicopter: 'en helicòpter',
          };
          return why === 'beyond_base'
            ? `La sortida (${how[what] ?? what}) queda a ${metres} m de l’objectiu, més lluny que la mateixa base: comproveu que el grup hi pot arribar.`
            : `La sortida (${how[what] ?? what}) queda a ${metres} m de l’objectiu, més lluny del que un grup hi arriba ${how[what] ?? what}: comproveu-la o moveu-la.`;
        }
        if (kind === 'no_helicopter_ground') {
          if (what === 'way_in') {
            return `Cap base a l’abast no té terreny pla on aterri un helicòpter a menys de 300 m: l’entrada és ${extra === 'vehicle' ? 'en vehicle' : 'a peu'} en lloc d’en helicòpter, i l’ordre ho diu.`;
          }
          return 'No hi ha terreny pla on aterri un helicòpter a menys de 300 m de la base: la base no té cap helicòpter i l’ordre no n’anuncia cap.';
        }
        if (kind === 'pickup_refused') {
          // pickup_refused:<no_ground|enemy|no_transport>:<foot|vehicle> (generator HelicopterPickup).
          const how = extra === 'vehicle' ? 'en vehicle' : 'a peu';
          const why: Record<string, string> = {
            no_ground:
              'no hi ha terreny pla i lliure on aterri un helicòpter prop del punt d’extracció',
            enemy:
              'l’únic terreny on aterraria un helicòpter prop del punt d’extracció és a menys de 500 m de l’enemic',
            no_transport: 'aquesta nit no hi ha cap helicòpter de transport per demanar',
          };
          return `Es va demanar sortir en helicòpter, però ${why[what] ?? what}: la sortida és ${how} fins al punt d’extracció marcat, i l’ordre ho diu.`;
        }
        if (kind === 'supports_without_ground') {
          const names = (what ?? '')
            .split(',')
            .map((k) =>
              k === 'medevac' ? 'evacuació mèdica' : k === 'transport' ? 'transport' : k,
            );
          return `Sense terreny on aterri un helicòpter prop de la base, la missió s’ha fet sense: ${names.join(', ')}.`;
        }
        if (kind === 'way_without_vehicle') {
          const nouns: Record<string, string> = {
            helicopter: 'cap helicòpter a la base ni cap suport de transport',
            boat: 'cap barca a la base',
            vehicle: 'cap vehicle a la base',
          };
          return `Es va demanar entrar o sortir ${what === 'helicopter' ? 'en helicòpter' : what === 'boat' ? 'en barca' : 'en vehicle'} i no hi ha ${nouns[what] ?? what}: l’ordre ho diu. Afegiu-ne un al pas «Sortida» o trieu una altra via.`;
        }
        return code;
      },
      terrain: 'Terreny',
      recipe: 'Recepta',
      downloadZip: 'Baixa (ZIP)',
      downloadPbo: 'Baixa (PBO)',
      downloadHint:
        'El ZIP es descomprimeix a Documents\\Arma 3\\mpmissions i s’obre a l’editor Eden; el PBO es deixa a la carpeta mpmissions d’un servidor. Cap dels dos porta el codi ni el testimoni d’una sessió.',
      duration: 'Durada',
      night: 'Moment del dia',
      isNight: 'Nit',
      isDay: 'Dia',
      players: 'Jugadors',
      authors: 'Autors',
      andMoreEditors: (n: number) => (n === 1 ? '+1 editor' : `+${n} editors`),
      whoDecides: 'Qui decideix què',
      timesPlayed: 'Vegades jugada',
      achieved: 'Partides guanyades',
      avgDuration: 'Durada mitjana',
      copies: 'Còpies i derivades',
      versions: 'Versions',
      versionsHint: 'L’historial complet és al dissenyador.',
      seeHistory: 'Veure l’historial',
      unknownAuthor: 'autor desconegut',
      noDecisionsYet: 'Cap decisió assignada encara.',
      requirements: 'Requisits',
      serverLoadsYes: 'Sí',
      aiGroups: (groups: number, men: number) => {
        const g = groups === 1 ? '1 grup' : `${groups} grups`;
        return men > 0 ? `${g}, ${men} homes o més` : g;
      },
      aiCost: 'Cost d’IA',
      headlessClient: 'Client sense interfície',
      clientMods: 'Mods que necessiten els jugadors',
      schedule: 'Programa una sessió',
      scheduleAt: 'Data i hora',
      scheduleGo: 'Programa',
      playNow: 'Juga ara',
      scheduleOpen: 'Programa…',
      moreActions: 'Més accions',
      deleteHint:
        'Surt de la biblioteca i del servidor. Les partides jugades i els seus informes es mantenen, i la pots recuperar des de la paperera.',
      deleteBlocked:
        'Hi ha una sessió preparada o en joc: acaba-la o cancel·la-la abans d’esborrar la missió.',
      share: 'Comparteix',
      shortCode: 'Codi curt',
      noSessionsYet: 'Encara no s’ha jugat cap sessió.',
      serverBusy: 'una sessió ja hi corre',
      serverMods: 'els mods no coincideixen',
      madeFor: 'Feta per al servidor',
      madeForNow: 'Aquell servidor, avui',
      serverSame: 'Porta els mateixos mods que quan es va fer la missió',
      serverGone: 'Ja no existeix al panell',
      serverChanged: (added: number, removed: number) =>
        `Ha canviat de mods: ${added} d’afegits, ${removed} de trets`,
      serverChangedWarn: (name: string) =>
        `El servidor «${name}», per al qual es va fer aquesta missió, ha canviat de mods des de llavors.`,
      serverGoneWarn: (name: string) =>
        `El servidor «${name}», per al qual es va fer aquesta missió, ja no existeix al panell.`,
      modsAdded: 'Afegits:',
      modsRemoved: 'Trets:',
      serverChangedHint:
        'La llista de sota diu quins servidors la poden jugar ara. Si no n’hi ha cap, se’n pot crear un amb els mods amb què es va fer.',
      serversFit: 'La poden jugar tal com és',
      serversAlmost: 'Tenen el mapa, però els falta alguna cosa (passa-hi el ratolí per veure què)',
      serversUnfit: 'No tenen el mapa',
      serverMissingClasses: (n: number) =>
        n === 1 ? 'li falta 1 classe de la missió' : `li falten ${n} classes de la missió`,
      serverUnread: 'mods sense llegir: no se sap',
      serverMissingMods: (n: number) =>
        n === 1 ? 'li falta 1 mod de la missió' : `li falten ${n} mods de la missió`,
      serverNoTerrain: 'no hi té el terreny',
      serverNotLinked: 'cal enllaçar-lo des de Servidors',
      noFitServer:
        'Cap servidor porta ara els mods d’aquesta missió. Pots crear-ne un amb els mods amb què es va fer, o triar-ne un de la llista i canviar-li els mods al panell.',
      createServer: 'Crea un servidor amb els mods d’aquesta missió',
      creatingServer: 'Creant el servidor…',
    },
    library: {
      filters: {
        terrain: 'Terreny',
        players: 'Jugadors',
        duration: 'Durada',
        recipe: 'Recepta',
        server: 'Feta per al servidor',
        notPlayed: 'Sense jugar encara',
      },
      allTerrains: 'Tots els terrenys',
      allRecipes: 'Totes les receptes',
      anyDuration: 'Qualsevol durada',
      durations: { short: 'Fins a 1 h', medium: '1 a 2 h', long: 'Més de 2 h' } as Record<
        string,
        string
      >,
      allServers: 'Tots els servidors',
      anyPlayers: 'Qualsevol nombre',
      sort: {
        label: 'Ordena',
        recent: 'Recents',
        mostPlayed: 'Més jugades',
        bestRated: 'Millor valorades',
      },
      importMission: 'Importa una missió',
      newTag: 'nova',
      newHint: 'Feta fa menys d’una setmana i encara sense jugar.',
      filtersLabel: 'Filtres',
      filtersN: (n: number) => `Filtres · ${n}`,
      select: 'Selecciona',
      selectDone: 'Surt de la selecció',
      tests: 'Mostra les proves',
      testsHint:
        'Les missions de les nits de fum, de les gires d’inspecció i de les proves guiades: la feina de la plataforma, no missions per jugar. Només les veu un administrador.',
      selectAll: (n: number) => `Selecciona-les totes (${n})`,
      selectNone: 'Cap',
      selectCard: (title: string) => `Selecciona «${title}»`,
      purgeMany: (n: number) =>
        n === 1 ? 'Esborra 1 missió del tot' : `Esborra ${n} missions del tot`,
      purgeManyTitle: (n: number) =>
        n === 1
          ? 'Esborrar del tot la missió seleccionada?'
          : `Esborrar del tot les ${n} missions seleccionades?`,
      purgeManyBody:
        'Les missions no van a la paperera: s’esborren amb totes les seves revisions i les seves sessions. Els informes del panell no es toquen; s’esborren des de Sessions. Una missió amb una sessió programada o en curs no s’esborra. No es pot desfer.',
      purgeOne: 'Esborra del tot',
      purgeOneLabel: (title: string) => `Esborra del tot «${title}»`,
      purgeOneTitle: (title: string) => `Esborrar del tot «${title}»?`,
      purgeOneBody:
        'La missió surt de la paperera i s’esborra amb totes les seves revisions i les seves sessions. No es pot desfer.',
      purging: (done: number, total: number) => `Esborrant ${done} de ${total}…`,
      purgeFailed: (n: number) =>
        n === 1 ? 'Una missió no s’ha pogut esborrar.' : `${n} missions no s’han pogut esborrar.`,
      loading: 'Carregant la biblioteca…',
      bin: 'Paperera',
      binEmpty: 'La paperera és buida.',
      deletedOn: (when: string) => `Esborrada el ${when}`,
      restore: 'Recupera',
      restored: 'Missió recuperada.',
      timesPlayed: (n: number) => (n === 1 ? '1 partida' : `${n} partides`),
      campaignTemplates: 'Plantilles de campanya',
      chapters: (n: number) => (n === 1 ? '1 capítol' : `${n} capítols`),
    },
  },
};
